import { useMemo } from 'react';
import { useTranslation } from "react-i18next";

const getRelativeTime = (seconds, t) => {
  let interval = 0;
  let unit = '';

  if (seconds < 60) {
    interval = Math.floor(seconds);
    if (interval !== 1) {
      unit = t("common.seconds", "seconds");
    } else {
      unit = t("common.second", "second");
    }
  } else if (seconds < 3600) {
    interval = Math.floor(seconds / 60);
    if (interval !== 1) {
      unit = t("common.minutes", "minutes");
    } else {
      unit = t("common.minute", "minute");
    }
  } else if (seconds < 86400) {
    interval = Math.floor(seconds / 3600);
    if (interval !== 1) {
      unit = t("common.hours", "hours");
    } else {
      unit = t("common.hour", "hour");
    }
  } else if (seconds < 604800) {
    interval = Math.floor(seconds / 86400);
    if (interval !== 1) {
      unit = t("common.days", "days");
    } else {
      unit = t("common.day", "day");
    }
  } else if (seconds < 2592000) {
    interval = Math.floor(seconds / 604800);
    if (interval !== 1) {
      unit = t("common.weeks", "weeks");
    } else {
      unit = t("common.week", "week");
    }
  } else if (seconds < 31536000) {
    interval = Math.floor(seconds / 2592000);
    if (interval !== 1) {
      unit = t("common.months", "months");
    } else {
      unit = t("common.month", "month");
    }
  } else {
    interval = Math.floor(seconds / 31536000);
    if (interval !== 1) {
      unit = t("common.years", "years");
    } else {
      unit = t("common.year", "year");
    }
  }

  return `${interval} ${unit} ${t("common.ago", "ago")}`;
};

const getShortAppliedTime = (date, now, locale, t) => {
  const diffDays = Math.round(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays <= 0) return `${t("common.applied", "Applied")} ${t("common.today", "today")}`;
  if (diffDays === 1) return `${t("common.applied", "Applied")} ${t("common.yesterday", "yesterday")}`;
  if (diffDays < 7) return `${t("common.applied", "Applied")} ${diffDays} ${t("common.days", "days")} ${t("common.ago", "ago")}`;
  if (diffDays < 14) return `${t("common.applied", "Applied")} ${t("common.lastWeek", "last week")}`;

  const sameYear = date.getFullYear() === now.getFullYear();
  return `${t("common.applied", "Applied")} ${date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  })}`;
};

function useTimeAgo(dateString) {
  const { t, i18n } = useTranslation();

  return useMemo(() => {
    if (!dateString) {
      return { short: '', long: '' };
    }

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return { short: '', long: '' };
    }

    const now = new Date();
    const locale = i18n?.language || "en-US";
    const seconds = Math.round(Math.abs(now.getTime() - date.getTime()) / 1000);
    const relativeTime = getRelativeTime(seconds, t);
    const longDate = date.toLocaleDateString(locale, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

    return {
      short: getShortAppliedTime(date, now, locale, t),
      long: `${t("common.AppliedOn", "Applied on")} ${longDate} (${relativeTime})`,
    };
  }, [dateString, i18n?.language, t]);

}

export default useTimeAgo; 